/**
 * The three questions every build feed asks of a step, answered once.
 *
 * Three skins render the same activity three different ways, and the markup
 * genuinely is theirs — a transcript row, a shell feed line, a mono log entry.
 * What is NOT theirs is which of those rows counts as a failure, what belongs on
 * the secondary line, and how much of a command's output to show before asking.
 * Those were written out verbatim in all three, which is the shape of a rule
 * about to be changed in two places out of three.
 *
 * The repo's rule (CLAUDE.md): logic in `lib/`, presentation in `skins/`, and
 * `lib/` never imports from `skins/`. `useStickToBottom` and `logGrowth` already
 * live here for the same reason. This is the rest of the same seam.
 */

import { describeElapsed } from "./labels.js";
import type { OutputLine, Step, StepIo } from "./viewModel.js";

export type StepTone = "failed" | "succeeded" | "neutral";

/**
 * How a step should read: as a failure, as the end of a good build, or as
 * neither.
 *
 * The artifact case is the one worth stating. A burst goes `failed` only when
 * EVERY write in it failed — a partial failure keeps the neutral tone and says
 * "N failed" in its own label, because painting a mostly-successful burst as a
 * failure would overstate it. `count` and `failedCount` are already netted by
 * the view model, so this reads them rather than recounting.
 */
export function stepTone(step: Step): StepTone {
  if (step.kind === "terminal") {
    return step.outcome === "succeeded" ? "succeeded" : "failed";
  }
  if (step.kind === "artifact" && (step.failedCount ?? 0) > 0 && step.count === 0) {
    return "failed";
  }
  return "neutral";
}

export interface StepDetail {
  /** Repeat count and elapsed time, already formatted, in reading order. */
  parts: string[];
  /** Short paths a burst wrote, for the skins that name them. */
  paths: string[];
  /** Writes in this burst beyond the ones named above. */
  hiddenPaths: number;
}

/**
 * The secondary line under a step: how many times, how long, and which files.
 *
 * This is where repetition goes. A count here is one row saying "this happened
 * four times"; without it the same sentence occupies four rows and the reader
 * cannot tell progress from a loop.
 *
 * `null` when there is nothing to say, so a caller can drop the element rather
 * than render an empty one.
 */
export function stepDetail(step: Step, active: boolean): StepDetail | null {
  const parts: string[] = [];

  // An artifact burst already says "6 files written"; repeating "×6" beside it
  // reports the same number twice.
  if (step.count > 1 && step.kind !== "artifact") parts.push(`×${step.count}`);
  if (step.elapsedMs !== undefined && (active || step.elapsedMs > 0)) {
    parts.push(describeElapsed(step.elapsedMs));
  }

  const paths = step.kind === "artifact" && step.count > 1 ? (step.paths ?? []) : [];
  if (parts.length === 0 && paths.length === 0) return null;

  return { parts, paths, hiddenPaths: Math.max(0, step.count - paths.length) };
}

/**
 * How much output a block shows before it needs asking.
 *
 * The tail, not the head: for a command still running the newest lines are the
 * ones that say where it got to, and for one that failed the error is at the
 * end. Six is about what fits without the block outweighing the step it
 * describes.
 */
export const VISIBLE_OUTPUT = 6;

export interface OutputWindow {
  shown: OutputLine[];
  /** Lines above the window, which is what an expand control offers. */
  hidden: number;
}

export function outputWindow(io: StepIo, expanded: boolean): OutputWindow {
  return {
    shown: expanded ? io.output : io.output.slice(-VISIBLE_OUTPUT),
    hidden: Math.max(0, io.output.length - VISIBLE_OUTPUT),
  };
}
